import { CircleHelp } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";

interface PageGuideTriggerProps {
  onClick: () => void;
  /** Compact trigger for embedding in nav pills (e.g. active breadcrumb). */
  variant?: "default" | "inline";
  className?: string;
  /** Disable hover/focus tooltip — use inside modals where focus can open the tooltip on mount. */
  showTooltip?: boolean;
}

export const PageGuideTrigger = ({
  onClick,
  variant = "default",
  className,
  showTooltip = true,
}: PageGuideTriggerProps) => {
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        variant === "inline"
          ? cn(
              "h-5 w-5 shrink-0 rounded-sm text-primary/60",
              "hover:bg-primary/10 hover:text-primary",
            )
          : cn(
              "h-8 w-8 shrink-0 rounded-full text-muted-foreground",
              "hover:bg-muted hover:text-foreground",
            ),
        className,
      )}
      aria-label="Open page guide"
    >
      <CircleHelp className={cn("shrink-0", variant === "inline" ? "h-3 w-3" : "h-4 w-4")} />
    </Button>
  );

  if (!showTooltip) return button;

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side={variant === "inline" ? "bottom" : "top"}
        align="center"
        className="max-w-[220px] text-xs"
      >
        Page guide — walk through how this page works.
      </TooltipContent>
    </Tooltip>
  );
};
